import { productStore } from './productDetail/productStore';
import { normaliseSeniority } from '../members/memberMock';

// Product fields with no backend column yet; the team is not among them, so a version carries none.
export const projectCodeKey = (productId: string): string => `mock:productCode:${productId}`;

/** A key with no version is what a version nobody has saved yet reads, so its first is not blank. */
export const inVersion = (key: string, version?: string): string => (version ? `${key}@${version}` : key);

// A version's own answer for a person's level; no entry means their profile's, until the product keeps one.
export const seniorityKey = (productId: string, version?: string): string =>
  inVersion(`productSeniority:${productId}`, version);

// A version's own chapter for a person; no entry means their profile's, until the product keeps one.
export const chapterStoreKey = (productId: string, version?: string): string =>
  inVersion(`productChapter:${productId}`, version);

const parseMap = (raw: string | null): Record<string, string> => {
  try {
    const value = JSON.parse(raw ?? '{}');
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  } catch {
    return {};
  }
};

// An unsaved version reads what was there before versions; a value never saved reads as nothing.
const readVersioned = (
  productId: string,
  key: (productId: string, version?: string) => string,
  version?: string,
): string | null => {
  try {
    return productStore.getItem(key(productId, version)) ?? productStore.getItem(key(productId));
  } catch {
    return null;
  }
};

export const loadProductSeniority = (productId: string, version?: string): Record<string, string> =>
  parseMap(readVersioned(productId, seniorityKey, version));

/** This version's level for a person, falling back to their profile's until the product keeps its own, even an empty one. */
export const effectiveSeniority = (override: string | undefined, joinedLevel: string): string =>
  override === '' ? '' : normaliseSeniority(override ?? '') || normaliseSeniority(joinedLevel);

export const loadProductChapters = (productId: string, version?: string): Record<string, string> =>
  parseMap(readVersioned(productId, chapterStoreKey, version));

/** This version's chapter for a person; an empty one is never picked, so it falls through to the profile's. */
export const effectiveChapter = (override: string | undefined, joinedChapter: string): string =>
  override || joinedChapter;
